import { useGetNoteQuery } from '@entities/clients/rtk-query.ts';
import type React from 'react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useClientNoteEditPageRedirect } from '@pages/clients/note/helpers.ts';
import { useViewClientNotePageRedirect } from '@pages/clients/note/view-note-page/helpers.ts';
import { EditAndDeleteDropdown } from '@widgets/shared/EditAndDeleteDropdown/index.ts';
import { useRemoveNote } from '@features/clients';
import { AuthorCard } from '@features/common/AuthorCard/index.tsx';
import { ArrowShortLeft } from '@shared/assets';
import { useIsAuthor } from '@shared/hooks/useIsAuthor.ts';
import { IconButton, ListBody, PageHead, PageTitle } from '@shared/ui';
import { DateViewer } from '@shared/ui/DateViewer/index.tsx';
import DescriptionViewer from '@shared/ui/viewer-note-editor-content/description-viewer/index.tsx';
import { Loader } from '@src/library/components/common';
import st from './styles.module.scss';

export const ViewNotePage: React.FC = () => {
  const { noteId } = useParams();
  const { redirectOnEditNote } = useClientNoteEditPageRedirect();
  const { doRedirectOnPrevPage } = useViewClientNotePageRedirect();
  const { removeNote } = useRemoveNote({ id: Number(noteId), onSuccess: doRedirectOnPrevPage });

  const { data: note, isLoading } = useGetNoteQuery(Number(noteId), {
    skip: !noteId || noteId === 'new',
  });
  const { isAuthor } = useIsAuthor({ authorId: note?.author.id });
  const handleClickBack = () => {
    doRedirectOnPrevPage('/clients/clients');
  };

  const handleClickEdit = () => {
    redirectOnEditNote(`/clients/clients/note/edit/${note?.id}`);
  };

  const NoteBody = useMemo(() => {
    if (isLoading) {
      return <Loader />;
    }
    if (note) {
      const { author, created, content } = note;
      return (
        <>
          <DateViewer className={st.date} value={created} dateVariant={'withMonth'} />
          <AuthorCard className={st.author} author={author} />
          <DescriptionViewer isAlwaysFull={true} description={content} />
        </>
      );
    }
    return 'Возникли технические неполадки (не пришёл объект заметки с бекенда)';
  }, [isLoading, note]);

  return (
    <>
      <PageTitle title="Заметка" />
      <PageHead
        Left={
          <IconButton variant="dark" onClick={handleClickBack} title="Вернуться назад">
            <ArrowShortLeft />
          </IconButton>
        }
        Right={
          isAuthor ? (
            <EditAndDeleteDropdown
              onDelete={() => {
                removeNote();
              }}
              onEdit={() => {
                handleClickEdit();
              }}
            />
          ) : undefined
        }
      />
      <ListBody className={st.listBody}>{NoteBody}</ListBody>
    </>
  );
};
